/**
 * Endet die Antwort mit einer Frage? (Ticket 2403)
 *
 * Martin: *„Wenn Vespera eine Antwort vorliest und diese mit einer Frage
 * endet, soll das Mikro automatisch freigeschaltet werden — ohne dass ich
 * jedes Mal den PTT-Knopf drücken muss."*
 *
 * ⚠️ DIE GRENZE IST DER PUNKT DES GANZEN. Ein Mikro, das nach JEDER Antwort
 *    aufgeht, ist Dauer-Zuhören — genau das, was das Ticket ausschliesst
 *    („Sollte NICHT bei jeder Antwort greifen"). Und die Vorgeschichte mahnt:
 *    die Freihand-Saga ist geparkt, und der Rekorder hat eine Historie
 *    ungewollter Dauer-Aufnahmen.
 *
 * ⚠️ Deshalb zählt nur eine Frage am Schluss, und die muss wirklich fragen.
 *
 * 23.09.2026 (Ticket 2522): „Am Schluss" heißt seitdem nicht mehr „der letzte
 * Satz". Zweimal an einem Tag fragte Vespera und hängte noch etwas an, und
 * das Mikro blieb zu: 11:16 zwei Nachsätze mit zusammen 36 Wörtern, 15:52
 * einer („Wenn ja, schreibe ich daraus das Konzept für Claude Code.").
 * Martin wollte es „egal wo" — das hätte die Grenze oben aufgehoben. Jetzt
 * zählt die letzte echte Frage im LETZTEN Absatz, wenn danach höchstens zwei
 * kurze Sätze kommen. Eine Frage weiter vorn in einer langen Antwort öffnet
 * weiterhin nichts.
 */

/** Was am Ende steht und keine Sprache ist: Emoji, Sternchen, Klammern. */
const SCHMUCK = /[\s*_~`"'»«„“”()\][]+$/u;

/**
 * Der letzte Satz eines Textes — ohne Markdown-Schmuck am Ende.
 *
 * Bewusst einfach: an Satzzeichen trennen und das letzte nicht-leere Stück
 * nehmen. Alles Feinere (Abkürzungen, Zahlen) würde hier mehr kaputt machen,
 * als es bringt — im Zweifel greift die Regel nicht, und das ist die
 * harmlose Richtung.
 */
export function letzterSatz(text: string): string {
  const s = saetze(text);
  return s[s.length - 1] ?? "";
}

/**
 * Die Sätze eines Textes, jeder ohne Schmuck am Ende. EINE Stelle für die
 * Satztrennung — letzterSatz und endetMitFrage teilen sie. (Ticket 2522)
 */
function saetze(text: string): string[] {
  return (text ?? "")
    .split(/(?<=[.!?…])\s+/)
    .map((s) => s.replace(SCHMUCK, "").trim())
    .filter(Boolean);
}

/**
 * So viele Sätze dürfen der Frage noch folgen. (23.09.2026, Ticket 2522)
 *
 * Zwei, weil der belegte Fall von 11:16 zwei hatte. Bei drei und mehr
 * beantwortet Vespera ihre Frage meist schon selbst.
 */
const NACHSAETZE_MAX = 2;

/**
 * Und zusammen höchstens so viele Wörter. (23.09.2026, Ticket 2522)
 *
 * Die längste belegte Nachrede (11:16) hatte 36. Fünfzig sind etwa
 * zwanzig Sekunden gesprochen — was länger ist, ist kein Nachsatz mehr,
 * sondern ein neuer Gedanke.
 */
const NACHSATZ_WOERTER_MAX = 50;

/** Die Wörter eines Satzes, ohne das Satzzeichen am Ende. */
function woerter(satz: string): string[] {
  return satz.replace(/[?!.…]+$/u, "").trim().split(/\s+/).filter(Boolean);
}

/**
 * Eine echte Frage: endet auf „?" und hat mindestens drei Wörter.
 *
 * ⚠️ Ein blosses Fragezeichen genuegt NICHT. „?" allein, „Ja?" oder ein
 *    Zitat, das zufaellig auf ein Fragezeichen endet, wuerden das Mikro
 *    oeffnen, ohne dass etwas gefragt wurde.
 */
function istEchteFrage(satz: string): boolean {
  return satz.endsWith("?") && woerter(satz).length >= 3;
}

/**
 * Der letzte Absatz — was nach der letzten Leerzeile steht.
 *
 * Eine Frage in einem früheren Absatz einer langen Antwort ist nicht die,
 * auf die jemand gleich antwortet.
 */
export function letzterAbsatz(text: string): string {
  const absaetze = (text ?? "").split(/\n\s*\n/);
  for (let i = absaetze.length - 1; i >= 0; i--) {
    const a = (absaetze[i] ?? "").replace(SCHMUCK, "").trim();
    if (a) return a;
  }
  return "";
}

/**
 * Endet der Text mit einer echten Frage — oder mit einer Frage und einem
 * kurzen Nachsatz?
 *
 * Gesucht wird im letzten Absatz von hinten die letzte echte Frage. Folgen
 * ihr höchstens NACHSAETZE_MAX Sätze mit zusammen höchstens
 * NACHSATZ_WOERTER_MAX Wörtern, zählt sie.
 *
 * ⚠️ Eine Frage in Anführungszeichen mitten im Satz wird dabei nie zu einem
 *    eigenen Satz: getrennt wird nur, wo das Satzzeichen direkt vor dem
 *    Leerraum steht, und bei „…?" steht dort das Anführungszeichen.
 */
export function endetMitFrage(text: string): boolean {
  const s = saetze(letzterAbsatz(text));
  const ende = s.length - 1;
  for (let i = ende; i >= 0 && ende - i <= NACHSAETZE_MAX; i--) {
    if (!istEchteFrage(s[i] ?? "")) continue;
    const nachWoerter = s.slice(i + 1).reduce((n, satz) => n + woerter(satz).length, 0);
    return nachWoerter <= NACHSATZ_WOERTER_MAX;
  }
  return false;
}

/*
 * WELLE 1 (jev-plan): EINE Stelle für „wartet Vespera am Ende auf Martin?“
 *
 * Frage mit Nachsatz (oben), blosses „?“ am Ende, Angebot/Bitte im Text und Angebot im
 * letzten Satz. Streng genug für „Mikro zu“: im Zweifel ja (= Mikro bleibt offen).
 * server/jev-abschluss.ts, server/jev.ts und server/offene-frage.ts verweisen hierher.
 */

/** Endet der Text auf ein Fragezeichen? Schluss-Schmuck (Leerraum, Anführungszeichen, Klammern, Emojis) zählt nicht. */
export function endetAufFragezeichen(text: string): boolean {
  const s = (text ?? "").replace(/[\s"'„“”‚‘’»«)\]}*_\p{Extended_Pictographic}\u{FE0F}]+$/gu, "");
  return s.endsWith("?") || s.endsWith("？");
}

/**
 * Angebote und Bitten in Vesperas Antwort — Muster, keine Satzliste. Wortgrenzen
 * mit \p{L}, nicht \b (\b kennt keine Umlaute).
 */
const ANGEBOT = new RegExp(
  [
    "(?<!\\p{L})(?:soll|möchtest|willst|magst|kann|darf) (?:ich|du|wir)(?!\\p{L})",
    "(?<!\\p{L})(?:sag|melde|schreib|ruf)(?:e)? (?:mir |mich |bescheid|kurz|einfach)",
    "(?<!\\p{L})(?:wenn du (?:willst|möchtest|magst|mehr|noch|etwas|was))(?!\\p{L})",
    "(?<!\\p{L})(?:lass (?:es )?mich wissen|gib (?:mir )?bescheid)(?!\\p{L})",
    "(?<!\\p{L})(?:welche[rsnm]?|welchen|was davon|wie viele|wann|wohin|wo)(?!\\p{L})[^.!?]*\\?",
    "(?<!\\p{L})(?:bitte (?:sag|nenn|schick|bestätige|wähle|entscheide))(?!\\p{L})",
  ].join("|"),
  "iu",
);

/** Bietet die Antwort etwas an oder bittet sie um etwas? */
export function bietetAn(antwort: string): boolean {
  return ANGEBOT.test(antwort ?? "");
}

/**
 * Angebot im LETZTEN Satz („Brauchst du sonst noch etwas“, „Ich kann dir auch …“). Geprüft wird nur der letzte
 * Satz: „Falls du rausgehst, nimm einen Schirm“ mitten in einer Auskunft ist kein Angebot.
 */
const ANGEBOT_ENDE = new RegExp(
  [
    "^(?:brauchst|benötigst|hättest|würdest|wünschst|interessiert|möchtest|willst|magst) (?:du|dich)(?!\\p{L})",
    "^(?:sonst|noch) (?:noch )?(?:etwas|was|jemand|ein)(?!\\p{L})",
    "^(?:nenn|sag|gib|schick|schreib)(?:e)? (?:mir|uns)(?!\\p{L})",
    "^frag(?:e)? (?:mich|einfach|gern)(?!\\p{L})",
    "^ich (?:kann|könnte|würde|schau|schaue|sehe|suche|lese|spiele|zeige)(?: dir| dich| mir| gern| gerne)* (?:auch|noch|gern|gerne|zusätzlich|außerdem|sonst)(?!\\p{L})",
    "^ich (?:schau|schaue|sehe|suche) (?:gern|gerne)(?!\\p{L})",
  ].join("|"),
  "iu",
);

/** Der letzte Satz einer Antwort ohne Schlusszeichen (trennt auch an „:“ und Zeilenumbruch). */
function letzterSatzOhneSchluss(antwort: string): string {
  const t = (antwort ?? "").trim().replace(/[.!?…\s"'“”„»«)\]]+$/u, "");
  const i = Math.max(t.lastIndexOf("."), t.lastIndexOf("!"), t.lastIndexOf("?"), t.lastIndexOf("…"), t.lastIndexOf("\n"), t.lastIndexOf(":"));
  return t.slice(i + 1).trim();
}

/** Warum Vespera am Ende wartet — `null`: sie wartet nicht. */
export type WartetGrund = "frage" | "angebot";

/** Wartet Vespera am Ende auf Martin, und woran sieht man es? */
export function wartetAmEndeGrund(antwort: string, nurSchluss = false): WartetGrund | null {
  const t = (antwort ?? "").trim();
  if (!t) return null;
  if (endetAufFragezeichen(t) || endetMitFrage(t)) return "frage";
  // `nurSchluss`: ein Satz wie „Sag Bescheid, wenn du das PDF hast“ mitten im Text zählt nicht (nach Martins abschließender Aussage, 04.10.).
  if ((!nurSchluss && bietetAn(t)) || ANGEBOT_ENDE.test(letzterSatzOhneSchluss(t))) return "angebot";
  return null;
}

/** Wartet Vespera am Ende auf Martin (Frage, Frage mit Nachsatz, Angebot, Bitte)? Im Zweifel ja. */
export function wartetAmEnde(antwort: string): boolean {
  return wartetAmEndeGrund(antwort) !== null;
}
